"use client";

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { DrawerDirection, Environment, Theme } from "../../../types";
import type { PreferredToken, TokenBalance, WalletInfoStyle } from "../types";
import type {
  EstimateGasFn,
  GetTransactionReceiptFn,
  LpBalanceReaderFn,
  PortfolioSection,
  SendTransactionFn,
  StakingRewardsReaderFn,
} from "../../wallet-portfolio/types";
import type { SendAccount, SendAsset } from "../../send";
import type { OnOutlink } from "../../../lib/outlink";
import type {
  BridgeApproveFn,
  BridgeHistoryItem,
  BridgeGetApprovalFn,
  BridgeGetToTokensFn,
  BridgeQuoteFn,
  BridgeSubmitFn,
  BridgeToken,
} from "../../bridge";

export type WalletInfoView =
  | "wallet"
  | "qr"
  | "portfolio"
  | "send"
  | "bridge"
  | "token-detail";

export interface WalletInfoContextValue {
  env: Environment;
  theme: Theme;
  isMobile: boolean;
  open: boolean;
  drawerDirection: DrawerDirection;
  modal: boolean;
  showBalance: boolean;
  showForgeToken: boolean;
  showGameToken: boolean;
  showQR: boolean;
  showBridge: boolean;
  qrLogoSrc?: string;
  walletAddress: string;
  accountName?: string;
  sendAccounts?: SendAccount[];
  profileImageUrl?: string;
  connectorName?: string;
  connectorIconUrl?: string;
  preferredTokens: PreferredToken[];
  close: () => void;
  onSelectWallet?: () => void;
  onCopyAddress?: (address: string, success: boolean) => void;
  onBuy?: () => void | Promise<void>;
  /**
   * Buy 카드를 시각적으로 비활성 상태로 표시하고, 사용자가 클릭하면 이 메시지를
   * 토스트로 띄운다. `onBuy`보다 우선. 예: on-ramp가 사용자 국가에서 차단된 경우.
   * `onBuy`가 reject된 경우(예: allow/url API 실패)에도 동일 토스트 메시지로 재사용.
   */
  onBuyDisabledMessage?: string;
  /**
   * Bridge 카드를 시각적으로 비활성 상태로 표시하고, 클릭하면 이 메시지를
   * 토스트로 띄운다. 브릿지 웹 이동보다 우선. 예: 연결된 지갑이 CROSSx
   * 계열이 아니라 브릿지를 지원하지 않는 경우.
   */
  onBridgeDisabledMessage?: string;
  /**
   * Send 카드(및 토큰 행 탭으로 진입하는 전송 플로우)를 시각적으로 비활성
   * 상태로 표시하고, 클릭하면 이 메시지를 토스트로 띄운다. `onSend` / 기본
   * 토큰 선택 진입보다 우선. 예: 연결된 지갑이 CROSSx 계열이 아닌 경우.
   */
  onSendDisabledMessage?: string;
  onDisconnect?: () => void;
  disconnectLabel: string;
  termsUrl?: string;
  termsLabel: string;
  privacyUrl?: string;
  privacyLabel: string;
  portalStyle?: WalletInfoStyle;
  navContent: ReactNode | null;
  navPosition: "top" | "bottom";
  setNavContent: (content: ReactNode | null) => void;
  setNavPosition: (position: "top" | "bottom") => void;
  footerContent: ReactNode | null;
  setFooterContent: (content: ReactNode | null) => void;
  view: WalletInfoView;
  setView: (view: WalletInfoView) => void;
  selectedSendToken: SendAsset | null;
  setSelectedSendToken: (token: SendAsset | null) => void;
  /** 토큰 상세/이력 화면에서 보여줄 토큰. 토큰 행 탭 시 설정된다. */
  selectedDetailToken: TokenBalance | null;
  setSelectedDetailToken: (token: TokenBalance | null) => void;
  showPortfolio: boolean;
  portfolioTitle: string;
  /**
   * 노출할 포트폴리오 섹션 필터. 미지정 시 전체 노출, 배열 지정 시 포함된
   * 섹션만 노출. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   */
  portfolioSections?: PortfolioSection[];
  showTotalAssets: boolean;
  totalAssetsLabel: string;
  /** Send 페이지의 일반 토큰 전송에 사용할 외부 트랜잭션 전송 함수. */
  sendTransaction?: SendTransactionFn;
  getTransactionReceipt?: GetTransactionReceiptFn;
  /**
   * Send 확인 단계에서 사용할 가스 추정 함수. SendPage로 그대로 전달된다.
   */
  estimateGas?: EstimateGasFn;
  /**
   * Terms/Privacy · 포트폴리오 섹션 등 외부 링크로 새 탭을 여는 모든 지점에서
   * 호출되는 가로채기 콜백. 반환값으로 URL 변형 / 취소가 가능하며 async도 지원.
   * 포트폴리오 영역은 `WalletPortfolioContext`로 그대로 릴레이된다.
   */
  onOutlink?: OnOutlink;
  /**
   * game-swap LP 섹션이 사용자별 LP 잔고/지분율을 온체인에서 보강하기 위한
   * 주입형 reader. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   */
  lpBalanceReader?: LpBalanceReaderFn;
  /**
   * CROSS Staking 섹션이 온체인 `earned()` / `balanceOf()`를 보강하기 위한
   * 주입형 reader. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   */
  stakingRewardsReader?: StakingRewardsReaderFn;
  bridgeTokens?: BridgeToken[];
  bridgeHistory?: BridgeHistoryItem[];
  getBridgeQuote?: BridgeQuoteFn;
  getBridgeToTokens?: BridgeGetToTokensFn;
  getBridgeApproval?: BridgeGetApprovalFn;
  approveBridge?: BridgeApproveFn;
  submitBridge?: BridgeSubmitFn;
  /**
   * 상단 QR 버튼 / 기본 액션 row의 Receive / Send 콜백. (Buy는 위
   * onBuy/onBuyDisabledMessage로 정의됨.)
   */
  onReceive?: () => void;
  /**
   * @deprecated Bridge 버튼은 항상 apps.json(gametokenBridge) 웹으로
   * 이동한다. 이 콜백은 더 이상 호출되지 않는다.
   */
  onBridge?: () => void;
  onSend?: () => void;
}

export const WalletInfoContext = createContext<WalletInfoContextValue | null>(
  null,
);

export function useWalletInfoContext() {
  const ctx = useContext(WalletInfoContext);
  if (!ctx) {
    throw new Error(
      "WalletInfo compound components must be used within <WalletInfo>",
    );
  }
  return ctx;
}
